import { getProductDetailQuoteListing } from "@bevco/Services/CommonService";
import { colorStrings } from "@bevco/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: any) => {
    const page = getCurrentPage(state);
    const pageType = page?.type;
    return {
        pageType,
        isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
    };
};

export interface ProductDetailsQuoteListingStyles {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
}

export const productDetailsQuoteListingStyles: ProductDetailsQuoteListingStyles = {
    wrapper: {
        css: css`
            margin-block: ;
            padding: 16px;
            background-color: ${getColor(colorStrings.dangerContrast)};
            border: 1px solid ${getColor(colorStrings.dangerMain)};

            .pdp-quote__listing {
                display: flex;
                flex-direction: column;
                list-style-type: none;
                padding: 0;
                margin: 0;
                gap: 12px;
                color: ${getColor(colorStrings.dangerMain)};
            }
        `,
    },
    heading: {
        variant: "h6",
        color: colorStrings.dangerMain,
        css: css`
            margin-block-end: 8px;
        `,
    },
};

type OwnProps = {
    productID: string;
};

type PDPQuoteListing = {
    Properties: {};
    Uri: string | null;
    erpOrderNumber: string | null;
    quoteUrl: string | null;
};

type QuoteListItem = {
    text: string | null;
    link: string | null;
};

type Props = OwnProps & WidgetProps & HasShellContext & HasProduct & ReturnType<typeof mapStateToProps>;

/** Share one in-flight request per ERP product number (Strict Mode remounts / duplicate widgets). */
const quoteListingInflight = new Map<string, Promise<QuoteListItem[]>>();
/** Cache successful results so remounts do not hit the API again. */
const quoteListingCache = new Map<string, QuoteListItem[]>();

const mapQuoteListingResult = (result: PDPQuoteListing[]): QuoteListItem[] =>
    result.map(item => ({
        text: item.erpOrderNumber,
        link: item.quoteUrl,
    }));

const loadQuoteListing = (productNumber: string): Promise<QuoteListItem[]> => {
    const cached = quoteListingCache.get(productNumber);
    if (cached) {
        return Promise.resolve(cached);
    }

    const existing = quoteListingInflight.get(productNumber);
    if (existing) {
        return existing;
    }

    const request = getProductDetailQuoteListing({ id: productNumber })
        .then(data => {
            if (!data.successful || !Array.isArray(data.result)) {
                return [] as QuoteListItem[];
            }
            return mapQuoteListingResult(data.result as PDPQuoteListing[]);
        })
        .catch(() => {
            // SQL timeouts / 400s should not surface as uncaught promise rejections.
            return [] as QuoteListItem[];
        })
        .then(items => {
            // Cache success and failure so remounts / Strict Mode do not re-hit a slow SP.
            quoteListingCache.set(productNumber, items);
            return items;
        })
        .finally(() => {
            quoteListingInflight.delete(productNumber);
        });

    quoteListingInflight.set(productNumber, request);
    return request;
};

const ProductDetailsQuoteListing = ({ isAuthenticated, productInfo, shellContext, pageType }: Props) => {
    const productId = productInfo.pricing?.additionalResults?.erpProductNumber;
    const styles = productDetailsQuoteListingStyles;
    const [quoteList, setQuoteList] = useState<QuoteListItem[]>([]);
    const { isInShell } = shellContext;
    const isPageDetail = pageType === "ProductDetailsPage";

    useEffect(() => {
        if (!isAuthenticated || !productId || isInShell) {
            return;
        }

        let cancelled = false;

        loadQuoteListing(productId).then(items => {
            if (!cancelled) {
                setQuoteList(items);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, productId, isInShell]);

    if (isInShell && isPageDetail) {
        return (
            <StyledWrapper
                css={css`
                    display: flex;
                    justify-content: center;
                `}
            >
                <Typography
                    css={css`
                        font-size: 16px;
                        color: #000;
                    `}
                >
                    Product Details Quote Listing Edit Mode
                </Typography>
            </StyledWrapper>
        );
    }

    if (!isAuthenticated || quoteList.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.heading}>{translate("ProductDetailQuoteListingHeading")}</Typography>
            <ul className="pdp-quote__listing">
                {quoteList.map((item, index) => (
                    <li key={index}>
                        <Link href={item.link ?? ""} target="_blank">
                            {item.text}
                        </Link>
                    </li>
                ))}
            </ul>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(withIsInShell(ProductDetailsQuoteListing as any) as any) as any),
    definition: {
        group: "Product Details",
        displayName: "Product Details Quote Listing",
        allowedContexts: ["ProductDetailsPage"],
    },
};
export default widgetModule;
